import type { AgentV2ConversationSummary } from '@/agent-v2/store/schemas'
import type { OutlookMessageIdentity } from './office-item'

const kStoragePrefix = 'paxton.outlook.v1'

export type OutlookPaneState = {
  selectedMatterId: string | null
  activeConversationIdsByMatter: Record<string, string>
}

const kEmptyState: OutlookPaneState = {
  selectedMatterId: null,
  activeConversationIdsByMatter: {},
}

export function getOutlookPaneStorageKey(email: string, identity: OutlookMessageIdentity): string {
  const thread = identity.conversationId ? ['thread', identity.conversationId] : ['message', identity.messageId]
  return `${kStoragePrefix}.${JSON.stringify([email.trim().toLowerCase(), identity.mailboxEmail.trim().toLowerCase(), ...thread])}`
}

export function readOutlookPaneState(email: string, identity: OutlookMessageIdentity | null, storage: Storage = window.localStorage): OutlookPaneState {
  if (!identity) return { ...kEmptyState }
  try {
    const value = JSON.parse(storage.getItem(getOutlookPaneStorageKey(email, identity)) ?? 'null') as Partial<OutlookPaneState> | null
    if (!value || typeof value !== 'object') return { ...kEmptyState }

    const activeConversationIdsByMatter = Object.fromEntries(
      Object.entries(value.activeConversationIdsByMatter ?? {}).filter(
        (entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string'
      )
    )
    return {
      selectedMatterId: typeof value.selectedMatterId === 'string' ? value.selectedMatterId : null,
      activeConversationIdsByMatter,
    }
  } catch {
    return { ...kEmptyState }
  }
}

export function writeOutlookPaneState(
  email: string,
  identity: OutlookMessageIdentity | null,
  state: OutlookPaneState,
  storage: Storage = window.localStorage
): void {
  if (!identity) return
  storage.setItem(getOutlookPaneStorageKey(email, identity), JSON.stringify(state))
}

export function clearOutlookPaneStates(): void {
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index--) {
      const key = window.localStorage.key(index)
      if (key?.startsWith(`${kStoragePrefix}.`)) window.localStorage.removeItem(key)
    }
  } catch {
    // Optional browser storage must not prevent Firebase sign-out.
  }
}

export function chooseOutlookConversation(
  conversations: AgentV2ConversationSummary[],
  storedConversationId: string | undefined
): AgentV2ConversationSummary | null {
  return conversations.find((conversation) => conversation.id === storedConversationId) ?? null
}

export function getVisibleOutlookHistory(conversations: AgentV2ConversationSummary[]): AgentV2ConversationSummary[] {
  return conversations.filter((conversation) => conversation.has_activity)
}
